import React, { useState, useEffect } from 'react';
import { CheckCircle2 as LucideCheck, DollarSign, Zap as LucideZap, Shield as LucideShield } from 'lucide-react';
import { getAuthHeadersSync } from '../../../../shared/utils/authHelpers';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';
import {
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Lightbulb as LightbulbIcon,
  FlashOn as ZapIcon,
  GpsFixed as TargetIcon
} from '@mui/icons-material';
import { WaterfallChart, ForcePlot, DependencePlot, InteractionHeatmap } from '../../../../components/ml/explainability';

// Mock data constant for fallback when no deployed models exist
const MOCK_FEATURE_IMPORTANCE = {
  'ip_reputation': 0.23,
  'alert_frequency': 0.18,
  'severity_score': 0.15,
  'time_of_day': 0.12,
  'source_country': 0.10
} as const;

// 🥷 WAVE 2 INTEGRATION: Performance Optimization Insights
interface PerformanceRecommendation {
  id: string;
  type: 'optimization' | 'warning' | 'success' | 'info';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  apb_ticket: string;
  metric_improvement: string;
  implementation_status: 'implemented' | 'in_progress' | 'planned';
}

interface Wave2PerformanceInsights {
  cache_performance: {
    hit_rate: number;
    avg_response_time: number;
    recommendation: string;
  };
  resource_efficiency: {
    memory_savings: number;
    tensor_optimization: number;
    recommendation: string;
  };
  batch_processing: {
    throughput_improvement: number;
    vectorization_speedup: number;
    recommendation: string;
  };
  ensemble_optimization: {
    speed_improvement: number;
    consensus_rate: number;
    recommendation: string;
  };
}

// 🥷 WAVE 5: APB-925 SHAP/LIME Explainability Insights
interface ExplainabilityInsight {
  id: string;
  model_name: string;
  explanation_method: 'SHAP' | 'LIME';
  confidence_score: number;
  explainability_score: number;
  top_features: Array<{
    feature: string;
    importance: number;
    contribution: 'positive' | 'negative';
    stability: number; // How consistent this feature is across explanations
  }>;
  model_interpretability: {
    global_feature_importance: Record<string, number>;
    feature_interactions: Array<{
      features: string[];
      interaction_strength: number;
    }>;
    decision_boundaries: {
      clarity: number;
      complexity: number;
    };
  };
  explanation_quality: {
    consistency: number;
    completeness: number;
    actionability: number;
  };
  generated_at: string;
  tenant_id: string;
}

// 🥷 WAVE 5: APB-926 Advanced Performance Analytics Insights
interface AdvancedAnalyticsInsight {
  id: string;
  analytics_type: 'performance' | 'trend' | 'anomaly' | 'prediction';
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'critical';
  metrics: {
    current_value: number;
    baseline_value: number;
    trend_direction: 'up' | 'down' | 'stable';
    confidence_interval: [number, number];
  };
  predictions: {
    next_24h: number;
    next_7d: number;
    next_30d: number;
  };
  recommendations: string[];
  business_impact: {
    cost_savings: number;
    efficiency_gain: number;
    risk_reduction: number;
  };
  generated_at: string;
}

interface Wave5ExplainabilityInsights {
  explainability_overview: {
    total_explanations: number;
    average_score: number;
    method_distribution: Record<string, number>;
    coverage_percentage: number;
  };
  model_interpretability: {
    most_interpretable_models: string[];
    least_interpretable_models: string[];
    global_feature_rankings: Array<{
      feature: string;
      importance: number;
      frequency: number;
    }>;
  };
  explanation_trends: {
    quality_trend: 'improving' | 'declining' | 'stable';
    consistency_trend: 'improving' | 'declining' | 'stable';
    coverage_trend: 'improving' | 'declining' | 'stable';
  };
  recent_insights: ExplainabilityInsight[];
}

interface Wave5AdvancedAnalytics {
  performance_analytics: {
    system_health_score: number;
    performance_grade: 'A' | 'B' | 'C' | 'D' | 'F';
    bottleneck_analysis: Array<{
      component: string;
      severity: number;
      impact: string;
    }>;
  };
  predictive_insights: {
    capacity_forecast: {
      days_until_capacity: number;
      projected_growth_rate: number;
      scaling_recommendations: string[];
    };
    anomaly_predictions: {
      likelihood_next_24h: number;
      risk_factors: string[];
      mitigation_strategies: string[];
    };
  };
  business_metrics: {
    cost_optimization_opportunities: number;
    efficiency_score: number;
    roi_projections: {
      monthly: number;
      quarterly: number;
      yearly: number;
    };
  };
  recent_analytics: AdvancedAnalyticsInsight[];
}

// 🥷 APB-870: ML Feedback Impact Visualization Interfaces
interface FeedbackImpactMetrics {
  total_feedback_items: number;
  feedback_types: Record<string, number>;
  average_confidence_improvement: number;
  model_performance_delta: {
    before_feedback: number;
    after_feedback: number;
    improvement_percentage: number;
  };
  retraining_triggers: {
    feedback_triggered: number;
    drift_triggered: number;
    scheduled_triggered: number;
  };
  parameter_tuning_results: {
    active_jobs: number;
    completed_jobs: number;
    average_improvement: number;
    best_hyperparameters: Record<string, unknown>;
  };
}

interface FeedbackVisualizationData {
  feedback_timeline: Array<{
    timestamp: string;
    feedback_count: number;
    confidence_score: number;
    model_performance: number;
  }>;
  feedback_distribution: {
    by_type: Record<string, number>;
    by_confidence: Record<string, number>;
    by_analyst: Record<string, number>;
  };
  impact_analysis: {
    immediate_impact: number;
    long_term_trend: 'improving' | 'stable' | 'declining';
    correlation_strength: number;
  };
}

interface MLFeedbackInsights {
  feedback_metrics: FeedbackImpactMetrics;
  visualization_data: FeedbackVisualizationData;
  recommendations: Array<{
    id: string;
    type: 'tuning' | 'retraining' | 'threshold_adjustment';
    priority: 'high' | 'medium' | 'low';
    description: string;
    expected_impact: string;
  }>;
  generated_at: string;
}

// 🥷 WAVE 3: APB-921 Drift Detection Insights
interface DriftAlert {
  id: string;
  timestamp: string;
  driftType: 'statistical' | 'performance' | 'concept';
  severity: 'moderate' | 'high' | 'critical';
  affectedModels: string[];
  threshold: number;
  currentValue: number;
  retrainingRecommended: boolean;
  autoRetrainingTriggered: boolean;
  description: string;
  recommendation: string;
}

interface Wave3DriftInsights {
  drift_monitoring_status: {
    enabled: boolean;
    last_check: string;
    next_check: string;
    active_alerts: number;
  };
  recent_drift_events: DriftAlert[];
  model_health_summary: {
    stable_models: number;
    degraded_models: number;
    critical_models: number;
    total_models: number;
  };
  auto_retraining_status: {
    enabled: boolean;
    last_retraining: string;
    cooldown_remaining: number;
    pending_retraining: string[];
  };
}

export const ModelExplainability: React.FC = () => {
  const [performanceInsights, setPerformanceInsights] = useState<Wave2PerformanceInsights | null>(null);
  const [recommendations, setRecommendations] = useState<PerformanceRecommendation[]>([]);
  
  // 🥷 WAVE 3: APB-921 Drift Detection State
  const [driftInsights, setDriftInsights] = useState<Wave3DriftInsights | null>(null);
  const [driftAlerts, setDriftAlerts] = useState<DriftAlert[]>([]);

  // 🥷 WAVE 5: APB-925 Explainability State
  const [explainabilityInsights, setExplainabilityInsights] = useState<Wave5ExplainabilityInsights | null>(null);
  const [explainabilityData, setExplainabilityData] = useState<ExplainabilityInsight[]>([]);

  // 🥷 WAVE 5: APB-926 Advanced Analytics State
  const [advancedAnalytics, setAdvancedAnalytics] = useState<Wave5AdvancedAnalytics | null>(null);
  const [analyticsInsights, setAnalyticsInsights] = useState<AdvancedAnalyticsInsight[]>([]);

  // 🥷 APB-870: ML Feedback Impact Visualization State
  const [mlFeedbackInsights, setMLFeedbackInsights] = useState<MLFeedbackInsights | null>(null);
  const [feedbackVisualizationData, setFeedbackVisualizationData] = useState<FeedbackVisualizationData | null>(null);
  const [loadingFeedbackInsights, setLoadingFeedbackInsights] = useState(false);

  // Real backend data states
  const [featureImportance, setFeatureImportance] = useState<Record<string, number>>({});
  const [featureImportanceLoading, setFeatureImportanceLoading] = useState(true);
  const [featureImportanceError, setFeatureImportanceError] = useState<string | null>(null);
  const [modelPerformanceMetrics, setModelPerformanceMetrics] = useState<{
    accuracy: number;
    precision: number;
    recall: number;
    f1_score: number;
  } | null>(null);

  // 🥷 WAVE 2: Load performance insights from BRAIN optimizations
  useEffect(() => {
    const loadWave2Insights = async () => {
      try {
        // ✅ REAL API CALL - Get actual performance metrics
        const baseUrl = getBaseUrl();
        const response = await fetch(`${baseUrl}/api/ml/performance/insights`, {
          headers: getAuthHeadersSync()
        });
        
        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }
        
        const data = await response.json();
        const insights: Wave2PerformanceInsights = {
          cache_performance: data.cache_performance,
          resource_efficiency: data.resource_efficiency,
          batch_processing: data.batch_processing,
          ensemble_optimization: data.ensemble_optimization
        };

        // ✅ REAL API CALL - Get recommendations
        const recsResponse = await fetch(`${baseUrl}/api/ml/performance/recommendations`, {
          headers: getAuthHeadersSync()
        });
        
        if (recsResponse.ok) {
          const recsData = await recsResponse.json();
          setRecommendations(recsData.recommendations);
        }
        
        setPerformanceInsights(insights);
        
      } catch (error) {
        console.error('Failed to load Wave 2 insights from API, using fallback:', error);
        
        // Fallback to mock data only on error
        const insights: Wave2PerformanceInsights = {
          cache_performance: {
            hit_rate: 85.2,
            avg_response_time: 45,
            recommendation: 'Redis caching achieving 85% hit rate with sub-50ms responses'
          },
          resource_efficiency: {
            memory_savings: 32.8,
            tensor_optimization: 28.5,
            recommendation: 'Memory pool management reducing usage by 33% with tensor optimization'
          },
          batch_processing: {
            throughput_improvement: 950,
            vectorization_speedup: 8.2,
            recommendation: 'Vectorized processing achieving 10x throughput with parallel workers'
          },
          ensemble_optimization: {
            speed_improvement: 27.3,
            consensus_rate: 78.4,
            recommendation: 'Smart voting with early consensus improving speed by 27%'
          }
        };
        setPerformanceInsights(insights);
      }
    };

    loadWave2Insights();
  }, []);

  // 🥷 WAVE 3: APB-921 Load drift detection insights
  useEffect(() => {
    const loadWave3DriftInsights = async () => {
      // Mock data representing drift detection status
      const insights: Wave3DriftInsights = {
        drift_monitoring_status: {
          enabled: true,
          last_check: '2025-07-23T14:25:00Z',
          next_check: '2025-07-23T15:25:00Z',
          active_alerts: 2
        },
        recent_drift_events: [
          {
            id: 'DRIFT-001',
            timestamp: '2025-07-23T14:25:00Z',
            driftType: 'statistical',
            severity: 'high',
            affectedModels: ['SVM', 'XGBoost', 'LSTM'],
            threshold: 0.05,
            currentValue: 0.02,
            retrainingRecommended: true,
            autoRetrainingTriggered: false,
            description: 'Statistical drift detected in feature distributions using Kolmogorov-Smirnov test',
            recommendation: 'Immediate retraining recommended to maintain model accuracy'
          },
          {
            id: 'DRIFT-002',
            timestamp: '2025-07-23T13:45:00Z',
            driftType: 'performance',
            severity: 'critical',
            affectedModels: ['Ensemble Model', 'GNN'],
            threshold: 0.10,
            currentValue: 0.15,
            retrainingRecommended: true,
            autoRetrainingTriggered: true,
            description: 'Performance degradation detected: 15% drop in accuracy metrics',
            recommendation: 'Auto-retraining initiated - monitor model performance closely'
          }
        ],
        model_health_summary: {
          stable_models: 15,
          degraded_models: 2,
          critical_models: 1,
          total_models: 18
        },
        auto_retraining_status: {
          enabled: true,
          last_retraining: '2025-07-23T12:30:00Z',
          cooldown_remaining: 4.5, // hours
          pending_retraining: ['Ensemble Model']
        }
      };

      const alerts: DriftAlert[] = insights.recent_drift_events;

      setDriftInsights(insights);
      setDriftAlerts(alerts);
    };

    loadWave3DriftInsights();
  }, []);

  // 🥷 APB-870: ML Feedback Impact Visualization Methods
  const loadMLFeedbackInsights = async (tenant_id: string = 'tenant-1') => {
    setLoadingFeedbackInsights(true);
    
    try {
      // Call BRAIN backend ML scoring insights API
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/api/ml/models/ml-scoring`, {
        method: 'POST',
        headers: getAuthHeadersSync(),
        body: JSON.stringify({
          timeframe: '24h',
          include_feedback_analysis: true,
          include_visualization_data: true
        })
      });
      
      if (response.ok) {
        const scoringData = await response.json();
        
        // Transform scoring data into feedback insights
        const feedbackInsights: MLFeedbackInsights = {
          feedback_metrics: {
            total_feedback_items: scoringData.total_alerts || 156,
            feedback_types: scoringData.feedback_distribution || {
              'True Positive': 89,
              'False Positive': 34,
              'Needs Review': 23,
              'Escalated': 10
            },
            average_confidence_improvement: scoringData.confidence_improvement || 12.5,
            model_performance_delta: {
              before_feedback: scoringData.baseline_performance || 0.78,
              after_feedback: scoringData.current_performance || 0.87,
              improvement_percentage: scoringData.improvement_percentage || 11.5
            },
            retraining_triggers: {
              feedback_triggered: 3,
              drift_triggered: 1,
              scheduled_triggered: 2
            },
            parameter_tuning_results: {
              active_jobs: 2,
              completed_jobs: 8,
              average_improvement: 8.3,
              best_hyperparameters: {
                learning_rate: 0.001,
                batch_size: 64,
                dropout_rate: 0.15
              }
            }
          },
          visualization_data: {
            feedback_timeline: scoringData.timeline || [
              { timestamp: '2025-07-24T00:00:00Z', feedback_count: 12, confidence_score: 0.82, model_performance: 0.85 },
              { timestamp: '2025-07-24T06:00:00Z', feedback_count: 18, confidence_score: 0.84, model_performance: 0.86 },
              { timestamp: '2025-07-24T12:00:00Z', feedback_count: 23, confidence_score: 0.87, model_performance: 0.87 },
              { timestamp: '2025-07-24T18:00:00Z', feedback_count: 19, confidence_score: 0.89, model_performance: 0.88 }
            ],
            feedback_distribution: {
              by_type: { 'True Positive': 57, 'False Positive': 22, 'Needs Review': 15, 'Escalated': 6 },
              by_confidence: { 'High (>0.8)': 68, 'Medium (0.5-0.8)': 24, 'Low (<0.5)': 8 },
              by_analyst: { 'analyst-1': 45, 'analyst-2': 32, 'analyst-3': 23 }
            },
            impact_analysis: {
              immediate_impact: 0.12,
              long_term_trend: 'improving',
              correlation_strength: 0.78
            }
          },
          recommendations: scoringData.recommendations || [
            {
              id: 'tune-learning-rate',
              type: 'tuning',
              priority: 'high',
              description: 'Reduce learning rate to 0.0008 for better convergence',
              expected_impact: '5-8% accuracy improvement'
            },
            {
              id: 'retrain-ensemble',
              type: 'retraining',
              priority: 'medium',
              description: 'Retrain ensemble model with recent feedback data',
              expected_impact: '3-5% false positive reduction'
            }
          ],
          generated_at: new Date().toISOString()
        };
        
        setMLFeedbackInsights(feedbackInsights);
        setFeedbackVisualizationData(feedbackInsights.visualization_data);
        
        console.log('🥷 ML Feedback insights loaded:', feedbackInsights);
        
      } else {
        console.warn('🥷 ML Feedback insights API unavailable, using mock data');
        
        // Fallback mock data
        const mockFeedbackInsights: MLFeedbackInsights = {
          feedback_metrics: {
            total_feedback_items: 156,
            feedback_types: {
              'True Positive': 89,
              'False Positive': 34,
              'Needs Review': 23,
              'Escalated': 10
            },
            average_confidence_improvement: 12.5,
            model_performance_delta: {
              before_feedback: 0.78,
              after_feedback: 0.87,
              improvement_percentage: 11.5
            },
            retraining_triggers: {
              feedback_triggered: 3,
              drift_triggered: 1,
              scheduled_triggered: 2
            },
            parameter_tuning_results: {
              active_jobs: 2,
              completed_jobs: 8,
              average_improvement: 8.3,
              best_hyperparameters: {
                learning_rate: 0.001,
                batch_size: 64,
                dropout_rate: 0.15
              }
            }
          },
          visualization_data: {
            feedback_timeline: [
              { timestamp: '2025-07-24T00:00:00Z', feedback_count: 12, confidence_score: 0.82, model_performance: 0.85 },
              { timestamp: '2025-07-24T06:00:00Z', feedback_count: 18, confidence_score: 0.84, model_performance: 0.86 },
              { timestamp: '2025-07-24T12:00:00Z', feedback_count: 23, confidence_score: 0.87, model_performance: 0.87 },
              { timestamp: '2025-07-24T18:00:00Z', feedback_count: 19, confidence_score: 0.89, model_performance: 0.88 }
            ],
            feedback_distribution: {
              by_type: { 'True Positive': 57, 'False Positive': 22, 'Needs Review': 15, 'Escalated': 6 },
              by_confidence: { 'High (>0.8)': 68, 'Medium (0.5-0.8)': 24, 'Low (<0.5)': 8 },
              by_analyst: { 'analyst-1': 45, 'analyst-2': 32, 'analyst-3': 23 }
            },
            impact_analysis: {
              immediate_impact: 0.12,
              long_term_trend: 'improving',
              correlation_strength: 0.78
            }
          },
          recommendations: [
            {
              id: 'tune-learning-rate',
              type: 'tuning',
              priority: 'high',
              description: 'Reduce learning rate to 0.0008 for better convergence',
              expected_impact: '5-8% accuracy improvement'
            },
            {
              id: 'retrain-ensemble',
              type: 'retraining',
              priority: 'medium',
              description: 'Retrain ensemble model with recent feedback data',
              expected_impact: '3-5% false positive reduction'
            }
          ],
          generated_at: new Date().toISOString()
        };
        
        setMLFeedbackInsights(mockFeedbackInsights);
        setFeedbackVisualizationData(mockFeedbackInsights.visualization_data);
      }
      
    } catch (error) {
      console.error('🥷 Error loading ML feedback insights:', error);
    } finally {
      setLoadingFeedbackInsights(false);
    }
  };
  
  // 🥷 APB-870: Load ML feedback insights on component mount
  useEffect(() => {
    loadMLFeedbackInsights();
  }, []);

  // Load Feature Importance from real backend
  useEffect(() => {
    const loadFeatureImportance = async () => {
      setFeatureImportanceLoading(true);
      setFeatureImportanceError(null);
      
      try {
        const baseUrl = getBaseUrl();
        console.log('🔍 Loading feature importance...');
        
        // Get first deployed model for feature importance
        const modelsResponse = await fetch(`${baseUrl}/api/models`, {
          headers: getAuthHeadersSync()
        });
        
        // ✅ CRITICAL FIX: Validate content type for models endpoint
        const modelsContentType = modelsResponse.headers.get('content-type');
        if (!modelsContentType || !modelsContentType.includes('application/json')) {
          console.error('❌ Models endpoint returned non-JSON:', modelsContentType);
          console.warn('⚠️ Using mock feature importance due to models fetch error');
          setFeatureImportance(MOCK_FEATURE_IMPORTANCE);
          setFeatureImportanceLoading(false);
          return;
        }
        
        if (!modelsResponse.ok) {
          console.error(`❌ Models endpoint returned ${modelsResponse.status}`);
          // Fallback to mock data
          setFeatureImportance(MOCK_FEATURE_IMPORTANCE);
          setFeatureImportanceLoading(false);
          return;
        }
        
        const modelsData = await modelsResponse.json();
        console.log('📊 Models fetched:', modelsData.models?.length || 0);
        
        const deployedModels = modelsData.models?.filter((m: any) => m.deployment_status === 'deployed') || [];
        console.log('✅ Deployed models:', deployedModels.length);
        
        const deployedModel = deployedModels[0];
        
        // ✅ CRITICAL FIX: Validate model exists AND has valid ID
        if (!deployedModel || !deployedModel.id || typeof deployedModel.id !== 'string' || deployedModel.id.trim() === '') {
          console.warn('⚠️ No valid deployed models found, using mock feature importance for demo', {
            hasModel: !!deployedModel,
            modelId: deployedModel?.id,
            modelIdType: typeof deployedModel?.id
          });
          setFeatureImportance(MOCK_FEATURE_IMPORTANCE);
          setFeatureImportanceLoading(false);
          return;
        }
        
        console.log('✅ Valid deployed model found:', {
          id: deployedModel.id,
          name: deployedModel.name,
          status: deployedModel.deployment_status
        });
        
        const response = await fetch(
          `${baseUrl}/api/ml/explain/${deployedModel.id}/feature-importance?max_features=5`,
          { headers: getAuthHeadersSync() }
        );
        
        // ✅ CRITICAL FIX: Validate content type before parsing JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const textResponse = await response.text();
          console.error('❌ Non-JSON response received:', {
            status: response.status,
            contentType,
            preview: textResponse.substring(0, 200)
          });
          
          throw new Error(
            `Server returned ${contentType || 'unknown'} instead of JSON. ` +
            `Status: ${response.status}. This usually indicates a server error or authentication issue.`
          );
        }
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ 
            detail: `HTTP ${response.status}` 
          }));
          throw new Error(errorData.detail || `Feature importance API returned ${response.status}`);
        }
        
        const data = await response.json();
        console.log('✨ Feature importance loaded:', Object.keys(data.feature_importance || {}).length, 'features');
        
        setFeatureImportance(data.feature_importance || {});
        setFeatureImportanceLoading(false);
        
      } catch (error: any) {
        console.error('❌ Failed to load feature importance:', error);
        
        // ✅ ENHANCED: Provide user-friendly error messages based on error type
        let userMessage = 'Failed to load feature importance';
        
        if (error.message.includes('JSON')) {
          userMessage = 'Server error - received invalid response. Please try again or contact support.';
          // Fallback to mock data for better UX
          setFeatureImportance(MOCK_FEATURE_IMPORTANCE);
        } else if (error.message.includes('404')) {
          userMessage = 'Model not found. Please ensure models are trained and deployed.';
          setFeatureImportance(MOCK_FEATURE_IMPORTANCE);
        } else if (error.message.includes('401') || error.message.includes('403')) {
          userMessage = 'Authentication error. Please refresh the page and try again.';
        } else if (error.message.includes('500')) {
          userMessage = 'Server error. Our team has been notified.';
          setFeatureImportance(MOCK_FEATURE_IMPORTANCE);
        } else {
          userMessage = error.message || 'Failed to load feature importance';
        }
        
        setFeatureImportanceError(userMessage);
        setFeatureImportanceLoading(false);
      }
    };
    
    loadFeatureImportance();
  }, []);

  // Load Model Performance Metrics from real backend
  useEffect(() => {
    const loadModelPerformance = async () => {
      try {
        const baseUrl = getBaseUrl();
        const response = await fetch(`${baseUrl}/api/ml/performance/model-metrics`, {
          headers: getAuthHeadersSync()
        });
        
        if (response.ok) {
          const data = await response.json();
          setModelPerformanceMetrics({
            accuracy: data.accuracy || 0,
            precision: data.precision || 0,
            recall: data.recall || 0,
            f1_score: data.f1_score || 0
          });
        }
      } catch (error) {
        console.error('Failed to load model performance metrics:', error);
      }
    };
    
    loadModelPerformance();
  }, []);

  // 🥷 WAVE 5: APB-925 Load explainability insights
  useEffect(() => {
    const loadWave5ExplainabilityInsights = async () => {
      try {
        // Integration with BRAIN Wave 5 explainability endpoints
        const baseUrl = getBaseUrl();
        const response = await fetch(`${baseUrl}/api/ml/models/explainability`, {
          headers: getAuthHeadersSync()
        });
        
        if (response.ok) {
          const data = await response.json();
          setExplainabilityInsights(data.insights);
          setExplainabilityData(data.recent_explanations || []);
        } else {
          // Fallback to mock data
          const mockInsights: Wave5ExplainabilityInsights = {
            explainability_overview: {
              total_explanations: 1247,
              average_score: 0.87,
              method_distribution: { SHAP: 65, LIME: 35 },
              coverage_percentage: 89.3
            },
            model_interpretability: {
              most_interpretable_models: ['RandomForest', 'LogisticRegression', 'SVM'],
              least_interpretable_models: ['GNN', 'LSTM', 'Autoencoder'],
              global_feature_rankings: [
                { feature: 'unusual_port_activity', importance: 0.34, frequency: 0.89 },
                { feature: 'time_of_day_anomaly', importance: 0.28, frequency: 0.76 },
                { feature: 'failed_login_attempts', importance: 0.26, frequency: 0.82 },
                { feature: 'data_volume', importance: 0.22, frequency: 0.71 }
              ]
            },
            explanation_trends: {
              quality_trend: 'improving',
              consistency_trend: 'stable',
              coverage_trend: 'improving'
            },
            recent_insights: [
              {
                id: 'EXP-001',
                model_name: 'NetworkAnomalyDetector',
                explanation_method: 'SHAP',
                confidence_score: 0.92,
                explainability_score: 0.89,
                top_features: [
                  { feature: 'unusual_port_activity', importance: 0.34, contribution: 'positive', stability: 0.91 },
                  { feature: 'time_of_day_anomaly', importance: 0.28, contribution: 'positive', stability: 0.87 }
                ],
                model_interpretability: {
                  global_feature_importance: { 'port_activity': 0.34, 'timing': 0.28 },
                  feature_interactions: [{ features: ['port_activity', 'timing'], interaction_strength: 0.23 }],
                  decision_boundaries: { clarity: 0.85, complexity: 0.42 }
                },
                explanation_quality: { consistency: 0.89, completeness: 0.91, actionability: 0.87 },
                generated_at: '2025-07-23T14:30:15Z',
                tenant_id: 'tenant-1'
              }
            ]
          };
          setExplainabilityInsights(mockInsights);
        }
      } catch (error) {
        console.error('🥷 Failed to load explainability insights:', error);
      }
    };

    loadWave5ExplainabilityInsights();
  }, []);

  // 🥷 WAVE 5: APB-926 Load advanced analytics insights
  useEffect(() => {
    const loadWave5AdvancedAnalytics = async () => {
      try {
        // Integration with BRAIN Wave 5 advanced analytics endpoints
        const baseUrl = getBaseUrl();
        const response = await fetch(`${baseUrl}/api/alerts/enrichment/advanced?timeframe=24h`, {
          headers: getAuthHeadersSync()
        });
        
        if (response.ok) {
          const data = await response.json();
          setAdvancedAnalytics(data.analytics);
          setAnalyticsInsights(data.insights || []);
        } else {
          // Fallback to mock data
          const mockAnalytics: Wave5AdvancedAnalytics = {
            performance_analytics: {
              system_health_score: 87.3,
              performance_grade: 'A',
              bottleneck_analysis: [
                { component: 'inference_pipeline', severity: 0.23, impact: 'Minor latency increase during peak hours' },
                { component: 'model_loading', severity: 0.15, impact: 'Memory pressure during concurrent model loads' }
              ]
            },
            predictive_insights: {
              capacity_forecast: {
                days_until_capacity: 45,
                projected_growth_rate: 0.12,
                scaling_recommendations: ['Add 2 inference nodes', 'Implement model caching']
              },
              anomaly_predictions: {
                likelihood_next_24h: 0.23,
                risk_factors: ['Peak traffic expected', 'Model retraining scheduled'],
                mitigation_strategies: ['Pre-warm caches', 'Scale inference capacity']
              }
            },
            business_metrics: {
              cost_optimization_opportunities: 15.7,
              efficiency_score: 89.2,
              roi_projections: { monthly: 12.5, quarterly: 38.2, yearly: 156.8 }
            },
            recent_analytics: [
              {
                id: 'ANA-001',
                analytics_type: 'performance',
                title: 'Inference Latency Optimization Opportunity',
                description: 'Model loading optimization could reduce P95 latency by 23%',
                severity: 'warning',
                metrics: {
                  current_value: 145,
                  baseline_value: 120,
                  trend_direction: 'up',
                  confidence_interval: [140, 150]
                },
                predictions: { next_24h: 148, next_7d: 152, next_30d: 160 },
                recommendations: ['Implement lazy loading', 'Add model caching layer'],
                business_impact: { cost_savings: 8.5, efficiency_gain: 23.0, risk_reduction: 15.2 },
                generated_at: '2025-07-23T14:25:00Z'
              }
            ]
          };
          setAdvancedAnalytics(mockAnalytics);
        }
      } catch (error) {
        console.error('🥷 Failed to load advanced analytics:', error);
      }
    };

    loadWave5AdvancedAnalytics();
  }, []);

  const getRecommendationIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircleIcon className="w-5 h-5 text-green-400" />;
      case 'warning': return <WarningIcon className="w-5 h-5 text-yellow-400" />;
      case 'optimization': return <ZapIcon className="w-5 h-5 text-purple-400" />;
      default: return <InfoIcon className="w-5 h-5 text-blue-400" />;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-400 bg-red-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'low': return 'text-green-400 bg-green-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">ML Model Insights</h1>
          <p className="text-gray-400">Performance + Explainability + Advanced Analytics</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm">
            <CheckCircleIcon className="w-4 h-4" />
            Performance Optimized
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-sm">
            <CheckCircleIcon className="w-4 h-4" />
            Advanced Analytics Active
          </div>
          <button className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700">
            Export Report
          </button>
        </div>
      </div>

      {/* 🥷 WAVE 2 PERFORMANCE ACHIEVEMENTS */}
      {performanceInsights && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SpeedIcon className="w-4 h-4 text-purple-400" />
              <h3 className="text-sm font-medium text-gray-400">Cache Hit Rate</h3>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-semibold text-white">{performanceInsights.cache_performance.hit_rate.toFixed(1)}%</div>
              <div className="text-sm text-green-400">↑ APB-915</div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Redis caching optimization</p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MemoryIcon className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-medium text-gray-400">Memory Savings</h3>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-semibold text-white">{performanceInsights.resource_efficiency.memory_savings.toFixed(1)}%</div>
              <div className="text-sm text-green-400">↑ APB-916</div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Tensor optimization gains</p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUpIcon className="w-4 h-4 text-green-400" />
              <h3 className="text-sm font-medium text-gray-400">Batch Throughput</h3>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-semibold text-white">{(performanceInsights.batch_processing.throughput_improvement / 100).toFixed(0)}x</div>
              <div className="text-sm text-green-400">↑ APB-917</div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Vectorized processing</p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <ZapIcon className="w-4 h-4 text-yellow-400" />
              <h3 className="text-sm font-medium text-gray-400">Ensemble Speed</h3>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-semibold text-white">{performanceInsights.ensemble_optimization.speed_improvement.toFixed(1)}%</div>
              <div className="text-sm text-green-400">↑ APB-918</div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Smart voting optimization</p>
          </div>
        </div>
      )}

      {/* 🥷 WAVE 2 PERFORMANCE RECOMMENDATIONS */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
          <LightbulbIcon className="w-5 h-5 text-yellow-400" />
          Performance Optimization Recommendations
        </h3>
        <div className="space-y-4">
          {recommendations.map((rec) => (
            <div key={rec.id} className="flex items-start gap-3 p-4 bg-gray-900/50 rounded-lg">
              <div className="flex-shrink-0 mt-0.5">
                {getRecommendationIcon(rec.type)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-white">{rec.title}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getImpactColor(rec.impact)}`}>
                    {rec.impact.toUpperCase()}
                  </span>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400">
                    {rec.apb_ticket}
                  </span>
                </div>
                <p className="text-sm text-gray-400 mb-2">{rec.description}</p>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-green-400 flex items-center gap-0.5"><LucideCheck size={12} /> {rec.metric_improvement}</span>
                  <span className="text-gray-500">Status: {rec.implementation_status.replace('_', ' ')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Model Performance */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Model Performance</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg">
              <span className="text-gray-300">Accuracy</span>
              <span className="font-medium text-green-400">
                {modelPerformanceMetrics?.accuracy.toFixed(1) || '0.0'}%
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg">
              <span className="text-gray-300">Precision</span>
              <span className="font-medium text-green-400">
                {modelPerformanceMetrics?.precision.toFixed(1) || '0.0'}%
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg">
              <span className="text-gray-300">Recall</span>
              <span className="font-medium text-green-400">
                {modelPerformanceMetrics?.recall.toFixed(1) || '0.0'}%
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg">
              <span className="text-gray-300">F1 Score</span>
              <span className="font-medium text-green-400">
                {modelPerformanceMetrics?.f1_score.toFixed(1) || '0.0'}%
              </span>
            </div>
          </div>
        </div>

        {/* Recent Predictions */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Recent Predictions</h3>
          <div className="space-y-4">
            <div className="p-4 bg-gray-900/50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium text-white">Malicious IP Detection</span>
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
                  High Confidence (98%)
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-400">
                IP: 203.0.113.42 - Detected as part of known botnet
              </p>
            </div>
            <div className="p-4 bg-gray-900/50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium text-white">Account Takeover Attempt</span>
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-500/20 text-yellow-400">
                  Medium Confidence (85%)
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-400">
                Unusual login pattern detected for user account
              </p>
            </div>
          </div>
        </div>

        {/* Feature Importance */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Feature Importance</h3>
          <div className="space-y-3">
            {featureImportanceLoading ? (
              <div className="text-center text-gray-400 py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <div className="text-sm">Loading feature importance...</div>
              </div>
            ) : featureImportanceError ? (
              <div className="text-center py-4">
                <div className="text-yellow-500 mb-2">⚠️ {featureImportanceError}</div>
                <button 
                  onClick={() => {
                    setFeatureImportanceError(null);
                    setFeatureImportanceLoading(true);
                    window.location.reload();
                  }}
                  className="text-sm text-blue-400 hover:text-blue-300 underline"
                >
                  Retry
                </button>
              </div>
            ) : Object.keys(featureImportance).length > 0 ? (
              Object.entries(featureImportance).map(([feature, importance]) => (
                <div key={feature} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">{feature}</span>
                    <span className="text-blue-400">{(importance * 100).toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${importance * 100}%` }}></div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-400 py-4">
                No feature importance data available
              </div>
            )}
          </div>
        </div>

        {/* Model Updates */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Model Updates</h3>
          <div className="space-y-4">
            <div className="p-4 bg-gray-900/50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium text-white">Latest Training</span>
                <span className="text-sm text-gray-400">2 hours ago</span>
              </div>
              <p className="mt-2 text-sm text-gray-400">
                Trained on 50,000 new data points with improved accuracy
              </p>
            </div>
            <div className="p-4 bg-gray-900/50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium text-white">Next Update</span>
                <span className="text-sm text-gray-400">In 4 hours</span>
              </div>
              <p className="mt-2 text-sm text-gray-400">
                Scheduled retraining with new feedback data
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 🥷 WAVE 3: APB-921 DRIFT DETECTION DASHBOARD */}
      {driftInsights && (
        <>
          <div className="mt-8 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUpIcon className="w-6 h-6 text-orange-400" />
              Model Drift Detection & Monitoring
            </h2>
            <p className="text-gray-400">Real-time drift detection with auto-retraining capabilities</p>
          </div>

          {/* Drift Monitoring Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Monitoring Status</h3>
                <div className={`w-3 h-3 rounded-full ${
                  driftInsights.drift_monitoring_status.enabled ? 'bg-green-400' : 'bg-red-400'
                }`} />
              </div>
              <p className="text-2xl font-semibold text-white">
                {driftInsights.drift_monitoring_status.enabled ? 'Active' : 'Inactive'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Last check: {new Date(driftInsights.drift_monitoring_status.last_check).toLocaleTimeString()}
              </p>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Active Alerts</h3>
                <WarningIcon className="w-5 h-5 text-orange-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {driftInsights.drift_monitoring_status.active_alerts}
              </p>
              <p className="text-xs text-gray-500 mt-1">Requiring attention</p>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Model Health</h3>
                <CheckCircleIcon className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {Math.round((driftInsights.model_health_summary.stable_models / driftInsights.model_health_summary.total_models) * 100)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {driftInsights.model_health_summary.stable_models}/{driftInsights.model_health_summary.total_models} models stable
              </p>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Auto-Retraining</h3>
                <ZapIcon className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {driftInsights.auto_retraining_status.enabled ? 'Enabled' : 'Disabled'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Cooldown: {driftInsights.auto_retraining_status.cooldown_remaining.toFixed(1)}h remaining
              </p>
            </div>
          </div>

          {/* Recent Drift Events */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 text-white">Recent Drift Events</h3>
            <div className="space-y-4">
              {driftAlerts.map((alert) => (
                <div key={alert.id} className="p-4 bg-gray-900/50 rounded-lg border-l-4 border-l-orange-400">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        alert.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                        alert.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {alert.severity.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-400 capitalize">{alert.driftType} Drift</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(alert.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-white font-medium mb-1">{alert.description}</p>
                  <p className="text-sm text-gray-400 mb-2">{alert.recommendation}</p>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-500">
                        Threshold: {(alert.threshold * 100).toFixed(1)}%
                      </span>
                      <span className="text-gray-500">
                        Current: {(alert.currentValue * 100).toFixed(1)}%
                      </span>
                      <span className="text-gray-500">
                        Models: {alert.affectedModels.join(', ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {alert.retrainingRecommended && (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                          Retraining Recommended
                        </span>
                      )}
                      {alert.autoRetrainingTriggered && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                          Auto-Retraining Active
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 🥷 WAVE 5: APB-925 EXPLAINABILITY INSIGHTS */}
      {explainabilityInsights && (
        <>
          <div className="mt-8 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <LightbulbIcon className="w-6 h-6 text-purple-400" />
              Model Explainability Insights (SHAP/LIME)
            </h2>
            <p className="text-gray-400">Advanced model interpretability with SHAP and LIME explanations - Now with interactive visualizations</p>
          </div>

          {/* Explainability Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Total Explanations</h3>
                <LightbulbIcon className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {explainabilityInsights.explainability_overview.total_explanations.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">Generated this month</p>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Avg Score</h3>
                <TargetIcon className="w-5 h-5 text-indigo-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {Math.round(explainabilityInsights.explainability_overview.average_score * 100)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">Explanation quality</p>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Coverage</h3>
                <CheckCircleIcon className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {explainabilityInsights.explainability_overview.coverage_percentage.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {explainabilityInsights.explainability_overview.coverage_percentage.toFixed(1)}% of models explained
              </p>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Primary Method</h3>
                <ZapIcon className="w-5 h-5 text-yellow-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {Object.keys(explainabilityInsights.explainability_overview.method_distribution)[0]}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {Object.values(explainabilityInsights.explainability_overview.method_distribution)[0]}% usage
              </p>
            </div>
          </div>

          {/* Global Feature Rankings */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 text-white">Global Feature Rankings</h3>
            <div className="space-y-3">
              {explainabilityInsights.model_interpretability.global_feature_rankings.map((feature, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">{index + 1}. {feature.feature}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">{feature.importance.toFixed(2)}</span>
                      <span className="text-xs text-gray-500">{Math.round(feature.frequency * 100)}% freq</span>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-900/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-blue-500" 
                      style={{ width: `${feature.importance * 100}%` }} 
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 🥷 NEW: ADVANCED SHAP VISUALIZATIONS */}
          <div className="mt-8 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4">Advanced SHAP Visualizations</h2>
            <p className="text-gray-400 mb-6">Interactive charts showing feature contributions, dependencies, and interactions</p>
          </div>

          {/* Waterfall Chart */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg mb-6">
            <WaterfallChart
              baseValue={0.50}
              features={[
                { name: 'unusual_port_activity', value: 8, contribution: 0.15 },
                { name: 'time_of_day_anomaly', value: '3:42 AM', contribution: 0.12 },
                { name: 'user_reputation', value: 'trusted', contribution: -0.08 },
                { name: 'failed_logins', value: 12, contribution: 0.06 },
                { name: 'data_volume', value: '2.3 GB', contribution: 0.04 }
              ]}
              finalPrediction={0.67}
            />
          </div>

          {/* Force Plot */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg mb-6">
            <ForcePlot
              baseValue={0.50}
              features={[
                { name: 'unusual_port_activity', contribution: 0.15 },
                { name: 'time_of_day_anomaly', contribution: 0.12 },
                { name: 'user_reputation', contribution: -0.08 },
                { name: 'failed_logins', contribution: 0.06 },
                { name: 'data_volume', contribution: 0.04 },
                { name: 'geo_distance', contribution: 0.03 }
              ]}
              finalPrediction={0.67}
            />
          </div>

          {/* Dependence Plot */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg mb-6">
            <DependencePlot
              featureName="unusual_port_activity"
              dataPoints={[
                { featureValue: 0, shapValue: -0.05 },
                { featureValue: 2, shapValue: 0.02 },
                { featureValue: 4, shapValue: 0.08 },
                { featureValue: 6, shapValue: 0.18 },
                { featureValue: 8, shapValue: 0.28 },
                { featureValue: 10, shapValue: 0.35 },
                { featureValue: 12, shapValue: 0.38 },
                { featureValue: 14, shapValue: 0.40 }
              ]}
            />
          </div>

          {/* Interaction Heatmap */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg mb-6">
            <InteractionHeatmap
              features={['port', 'time', 'login', 'volume', 'geo']}
              interactions={[
                [0.0, 0.42, 0.28, 0.15, 0.08],
                [0.42, 0.0, 0.38, 0.25, 0.12],
                [0.28, 0.38, 0.0, 0.35, 0.22],
                [0.15, 0.25, 0.35, 0.0, 0.45],
                [0.08, 0.12, 0.22, 0.45, 0.0]
              ]}
            />
          </div>
        </>
      )}

      {/* 🥷 WAVE 5: APB-926 ADVANCED ANALYTICS INSIGHTS */}
      {advancedAnalytics && (
        <>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUpIcon className="w-6 h-6 text-blue-400" />
              Advanced Performance Analytics
            </h2>
            <p className="text-gray-400">Predictive insights with business impact analysis</p>
          </div>

          {/* Performance Analytics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">System Health</h3>
                <CheckCircleIcon className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {advancedAnalytics.performance_analytics.system_health_score.toFixed(1)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Grade: {advancedAnalytics.performance_analytics.performance_grade}
              </p>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Efficiency Score</h3>
                <SpeedIcon className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {advancedAnalytics.business_metrics.efficiency_score.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">Operational efficiency</p>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-400">Cost Optimization</h3>
                <TrendingUpIcon className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-semibold text-white">
                {advancedAnalytics.business_metrics.cost_optimization_opportunities.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">Potential savings</p>
            </div>
          </div>

          {/* Predictive Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
                <TrendingUpIcon className="w-5 h-5 text-blue-400" />
                Capacity Forecast
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg">
                  <span className="text-gray-300">Days Until Capacity</span>
                  <span className="font-medium text-yellow-400">
                    {advancedAnalytics.predictive_insights.capacity_forecast.days_until_capacity} days
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg">
                  <span className="text-gray-300">Growth Rate</span>
                  <span className="font-medium text-blue-400">
                    {(advancedAnalytics.predictive_insights.capacity_forecast.projected_growth_rate * 100).toFixed(1)}%/month
                  </span>
                </div>
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Scaling Recommendations</h4>
                  <div className="space-y-1">
                    {advancedAnalytics.predictive_insights.capacity_forecast.scaling_recommendations.map((rec, idx) => (
                      <div key={idx} className="text-xs text-gray-300 flex items-center gap-2">
                        <CheckCircleIcon className="w-3 h-3 text-green-400" />
                        {rec}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
                <WarningIcon className="w-5 h-5 text-orange-400" />
                Anomaly Predictions
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg">
                  <span className="text-gray-300">Next 24h Risk</span>
                  <span className="font-medium text-orange-400">
                    {Math.round(advancedAnalytics.predictive_insights.anomaly_predictions.likelihood_next_24h * 100)}%
                  </span>
                </div>
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Risk Factors</h4>
                  <div className="space-y-1">
                    {advancedAnalytics.predictive_insights.anomaly_predictions.risk_factors.map((factor, idx) => (
                      <div key={idx} className="text-xs text-gray-300 flex items-center gap-2">
                        <WarningIcon className="w-3 h-3 text-orange-400" />
                        {factor}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Mitigation Strategies</h4>
                  <div className="space-y-1">
                    {advancedAnalytics.predictive_insights.anomaly_predictions.mitigation_strategies.map((strategy, idx) => (
                      <div key={idx} className="text-xs text-gray-300 flex items-center gap-2">
                        <CheckCircleIcon className="w-3 h-3 text-green-400" />
                        {strategy}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Analytics Insights */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
              <LightbulbIcon className="w-5 h-5 text-yellow-400" />
              Recent Analytics Insights
            </h3>
            <div className="space-y-4">
              {advancedAnalytics.recent_analytics.map((insight) => (
                <div key={insight.id} className="flex items-start gap-3 p-4 bg-gray-900/50 rounded-lg">
                  <div className="flex-shrink-0 mt-0.5">
                    {insight.severity === 'critical' ? (
                      <WarningIcon className="w-5 h-5 text-red-400" />
                    ) : insight.severity === 'warning' ? (
                      <WarningIcon className="w-5 h-5 text-yellow-400" />
                    ) : (
                      <InfoIcon className="w-5 h-5 text-blue-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-white">{insight.title}</h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        insight.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                        insight.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {insight.severity.toUpperCase()}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400">
                        {insight.analytics_type.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{insight.description}</p>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-green-400 flex items-center gap-0.5"><DollarSign size={12} /> ${insight.business_impact.cost_savings}K savings</span>
                      <span className="text-blue-400 flex items-center gap-0.5"><LucideZap size={12} /> {insight.business_impact.efficiency_gain}% efficiency</span>
                      <span className="text-purple-400 flex items-center gap-0.5"><LucideShield size={12} /> {insight.business_impact.risk_reduction}% risk reduction</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ModelExplainability;
